import { FC, useState } from "react"
import { observer } from "mobx-react"
import { useStore } from "stores/utils/use-store"
import { Alert, Button, Card, Form, Input, Modal, Popconfirm, Space, Table, Tag, Typography } from "antd"
import { RecordingCutFlag, RecordingCutFlagInput } from "../store/types"
import { DEFAULT_VOID_REASON, MAX_CUT_FLAG_LOOKUP_IDS } from "../gameplay-cut"
import { RecordingIdsInput } from "./recording-ids-input"

interface FlagRow {
  recordingId: string
  flag: RecordingCutFlag | null
}

export function cutFlagRows(lookedUp: string[], flags: RecordingCutFlag[]): FlagRow[] {
  const byId = new Map(flags.map(f => [f.recordingId, f]))
  const ids = lookedUp.length ? lookedUp : flags.map(f => f.recordingId)

  return ids.map(recordingId => ({ recordingId, flag: byId.get(recordingId) ?? null }))
}

export const CutVoidFlags: FC = observer(() => {
  const { dataStore: { dataDeliveryStore: store } } = useStore()
  const [form] = Form.useForm<RecordingCutFlagInput>()
  const [recordingIds, setRecordingIds] = useState<string[]>([])
  const [lookedUp, setLookedUp] = useState<string[]>([])
  const [target, setTarget] = useState<FlagRow | null>(null)
  const [saving, setSaving] = useState(false)
  const [failed, setFailed] = useState(false)

  const onLookup = async() => {
    const ids = recordingIds
    const result = await store.loadCutFlags(ids)
    if (result === "stale") return
    setFailed(result === "failed")
    if (result === "ok") setLookedUp(ids)
  }

  const onRecent = async() => {
    const result = await store.loadRecentCutFlags()
    if (result === "stale") return
    setFailed(result === "failed")
    if (result === "ok") setLookedUp([])
  }

  const onSave = async() => {
    if (!target) return
    let input: RecordingCutFlagInput
    try {
      input = await form.validateFields()
    } catch {
      return
    }
    setSaving(true)
    const saved = await store.setCutFlag(target.recordingId, input)
    setSaving(false)
    if (saved) setTarget(null)
  }

  const columns = [
    {
      title: "Recording",
      dataIndex: "recordingId",
      key: "recordingId",
      render: (id: string) => <span style={{ fontFamily: "monospace", fontSize: 12 }}>{id}</span>,
    },
    {
      title: "Void reason",
      key: "voidReason",
      render: (_: unknown, r: FlagRow) => (r.flag ? <Tag color="red">{r.flag.voidReason}</Tag> : <Tag>not flagged</Tag>),
    },
    { title: "Note", key: "note", render: (_: unknown, r: FlagRow) => r.flag?.note ?? "—" },
    { title: "Flagged by", key: "flaggedBy", render: (_: unknown, r: FlagRow) => r.flag?.flaggedBy ?? "—" },
    {
      title: "Updated",
      key: "updatedAt",
      render: (_: unknown, r: FlagRow) => (r.flag ? new Date(r.flag.updatedAt).toLocaleString() : "—"),
    },
    {
      title: "",
      key: "actions",
      render: (_: unknown, r: FlagRow) => (
        <Space>
          <Button size="small" onClick={() => setTarget(r)}>{r.flag ? "Edit flag" : "Flag"}</Button>
          {r.flag && (
            <Popconfirm title="Clear this flag?" onConfirm={() => store.clearCutFlag(r.recordingId)}>
              <Button size="small" danger>Clear</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  return (
    <Card title="Cut void flags">
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        A flagged recording is voided by the gameplay cut: nothing of it is kept.
      </Typography.Paragraph>
      <Space direction="vertical" style={{ width: "100%" }}>
        <RecordingIdsInput
          max={MAX_CUT_FLAG_LOOKUP_IDS}
          placeholder={`Recording ids (up to ${MAX_CUT_FLAG_LOOKUP_IDS}), separated by commas or new lines`}
          onChange={parsed => setRecordingIds(parsed.ids)}
        />
        <Space>
          <Button
            type="primary"
            onClick={onLookup}
            disabled={!recordingIds.length || recordingIds.length > MAX_CUT_FLAG_LOOKUP_IDS}
          >
            Look up
          </Button>
          <Button onClick={onRecent}>Show recent flags</Button>
        </Space>
        {failed && (
          <Alert
            type="error"
            showIcon
            message="Loading flags failed. The rows below are from the previous successful load."
          />
        )}
        <Table
          size="small"
          rowKey="recordingId"
          loading={store.isLoading}
          dataSource={cutFlagRows(lookedUp, store.cutFlags)}
          columns={columns}
          pagination={{ pageSize: 50 }}
        />
      </Space>

      <Modal
        open={!!target}
        title={`Flag ${target?.recordingId ?? ""}`}
        okText="Save flag"
        confirmLoading={saving}
        onOk={onSave}
        onCancel={() => setTarget(null)}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          preserve={false}
          initialValues={{ voidReason: target?.flag?.voidReason ?? DEFAULT_VOID_REASON, note: target?.flag?.note ?? "" }}
        >
          <Form.Item name="voidReason" label="Void reason" rules={[{ required: true, whitespace: true, max: 100 }]}>
            <Input />
          </Form.Item>
          <Form.Item name="note" label="Note" rules={[{ max: 2000 }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  )
})
